import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import LockClockIcon from '@mui/icons-material/LockClock';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalculateIcon from '@mui/icons-material/Calculate';
import ViewListIcon from '@mui/icons-material/ViewList';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { Roles } from '../constants';

/**
 * Master menu item definitions.
 * Each item specifies which roles can see it and an optional featureFlag key
 * that must be enabled for the item to appear.
 */
const allMenuItems = [
  // ── Lender / Admin ──
  { id: 'lender-dashboard', text: 'Dashboard', icon: DashboardIcon, path: '/lender/dashboard', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Overview' },
  { id: 'ae-dashboard', text: 'AE Dashboard', icon: BarChartIcon, path: '/ae/dashboard', roles: [Roles.Admin, Roles.LenderAdmin, Roles.LoanOfficer, Roles.AccountExecutive], group: 'Overview' },
  { id: 'lender-scenario-pricer', text: 'Quick Pricer', icon: CalculateIcon, path: '/lender/scenario-pricer', roles: [Roles.Admin, Roles.LenderAdmin], featureFlag: 'scenarioPricer', group: 'Loans' },
  { id: 'lender-brokers', text: 'Broker Companies', icon: BusinessIcon, path: '/lender/brokers', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Organization' },
  { id: 'lender-users', text: 'Users', icon: GroupIcon, path: '/lender/users', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Organization' },
  { id: 'lender-retail-pipeline', text: 'Retail Pipeline', icon: HomeWorkIcon, path: '/lender/retail-pipeline', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Loans' },
  { id: 'lender-vesta-contacts', text: 'UFF Contact Sync', icon: SyncAltIcon, path: '/lender/vesta-contacts', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Admin' },
  { id: 'lender-lock-desk', text: 'Lock Desk', icon: LockClockIcon, path: '/lender/lock-desk', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Admin' },
  { id: 'lender-feature-management', text: 'Feature Management', icon: ToggleOnIcon, path: '/lender/feature-management', roles: [Roles.Admin], group: 'Admin' },

  // ── Account Executive ──
  { id: 'ae-pipeline', text: 'AE Pipeline', icon: ListAltIcon, path: '/ae/pipeline', roles: [Roles.LoanOfficer], group: 'Loans' },
  { id: 'ae-scenario-pricer', text: 'Quick Pricer', icon: CalculateIcon, path: '/ae/scenario-pricer', roles: [Roles.LoanOfficer, Roles.AccountExecutive], featureFlag: 'scenarioPricer', group: 'Loans' },

  // ── Broker ──
  { id: 'broker-scenario-pricer', text: 'Quick Pricer', icon: CalculateIcon, path: '/broker/scenario-pricer', roles: [Roles.BrokerAdmin, Roles.Broker, Roles.Processor, Roles.BrokerFullAccessReadOnly], featureFlag: 'scenarioPricer', group: 'Loans' },
  { id: 'broker-pipeline', text: 'Pipeline', icon: ViewListIcon, path: '/broker/pipeline', roles: [Roles.BrokerAdmin, Roles.Broker, Roles.Processor, Roles.BrokerFullAccessReadOnly], group: 'Loans' },
  { id: 'broker-company', text: 'Manage Company', icon: ApartmentIcon, path: '/broker/company', roles: [Roles.BrokerAdmin], group: 'Organization' },
  { id: 'broker-company-view', text: 'Company', icon: ApartmentIcon, path: '/broker/company', roles: [Roles.Processor], group: 'Organization' },
  { id: 'broker-compensation', text: 'Compensation', icon: AttachMoneyIcon, path: '/broker/compensation', roles: [Roles.BrokerAdmin], group: 'Organization' },
];

/** Group display order for the sidebar. */
export const MENU_GROUP_ORDER = ['Overview', 'Loans', 'Organization', 'Admin'];

/**
 * Returns the filtered menu items for the given role.
 * @param {string} role - The user's role string (e.g. 'Admin', 'BrokerAdmin')
 * @param {Object} featureFlags - Feature flag map from the configuration API
 * @returns {Array} Menu items the user should see
 */
export const getMenuItemsForRole = (role, featureFlags = {}, options = {}) => {
  const { pendingBroker = false } = options;
  return allMenuItems.filter((item) => {
    if (!item.roles.includes(role)) return false;
    if (item.featureFlag && featureFlags[item.featureFlag] !== true) return false;
    if (pendingBroker && item.id !== 'broker-scenario-pricer') return false;
    return true;
  });
};

/**
 * Human-readable role labels for the user dropdown.
 */
export const roleLabels = {
  [Roles.Admin]: 'Administrator',
  [Roles.LenderAdmin]: 'Lender Admin',
  [Roles.LoanOfficer]: 'Account Executive',
  [Roles.BrokerAdmin]: 'Broker Admin',
  [Roles.Broker]: 'Loan Officer',
  [Roles.Processor]: 'Processor',
  [Roles.BrokerFullAccessReadOnly]: 'Read-only',
  [Roles.AccountExecutive]: 'Account Executive',
};
